import { useMemo } from 'react'
import type { DraftState, PickState, PlayerSummary } from '../../types/protocol'

// The whole grid, small enough to sit in a panel. Shared verbatim by the
// control panel and the owner's Board tab so the two are literally the same
// board — two implementations would drift.

export default function BoardPanel({
  draft,
  players,
}: {
  draft: DraftState
  players: PlayerSummary[]
}) {
  const playersById = useMemo(
    () => new Map(players.map((player) => [player.id, player])),
    [players],
  )
  const rounds = draft.rounds || 1

  const byRound = useMemo(() => {
    const grid = new Map<number, PickState[]>()
    draft.picks.forEach((pick) => {
      const row = grid.get(pick.round) ?? []
      row.push(pick)
      grid.set(pick.round, row)
    })
    return grid
  }, [draft.picks])

  if (draft.picks.length === 0) return null

  return (
    <section className="panel p-4 sm:p-5">
      <h2 className="eyebrow">Board</h2>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full min-w-max border-separate border-spacing-0.5 text-xs">
          <thead>
            <tr>
              <th className="px-1 text-left font-mono text-[10px] font-normal text-ink-lo">R</th>
              {draft.teams.map((team) => (
                <th
                  key={team.id}
                  className="max-w-24 truncate px-1 text-left font-display text-[13px] font-semibold text-ink-mid"
                >
                  {team.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: rounds }, (_, index) => index + 1).map((round) => (
              <tr key={round}>
                <td className="px-1 font-mono text-[10px] text-ink-lo tabular-nums">{round}</td>
                {draft.teams.map((team) => {
                  const pick = byRound.get(round)?.find((p) => p.team_id === team.id)
                  return (
                    <BoardCell key={team.id} draft={draft} pick={pick} playersById={playersById} />
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

function BoardCell({
  draft,
  pick,
  playersById,
}: {
  draft: DraftState
  pick: PickState | undefined
  playersById: Map<number, PlayerSummary>
}) {
  if (!pick) return <td className="px-1" />

  const onClock = pick.overall === draft.current_pick_overall
  const player = pick.player_id != null ? playersById.get(pick.player_id) : undefined

  // A skip is an absence, not a warning — it loses its old amber and reads as
  // dimmed-out instead, which leaves warm colour to mean "attention" alone.
  let body = '—'
  let tone = 'text-ink-lo'
  if (pick.status === 'made') {
    body = player?.full_name ?? `#${pick.player_id}`
    tone = 'text-ink-hi'
  } else if (pick.status === 'skipped') {
    body = 'skipped'
    tone = 'text-ink-lo italic'
  } else if (onClock) {
    body = 'on the clock'
    tone = 'text-signal'
  }

  return (
    <td
      className={`max-w-24 truncate rounded-sm px-1 py-0.5 ${tone} ${
        onClock ? 'bg-signal/10 shadow-[inset_0_0_0_1px_var(--color-signal)]' : 'bg-ink/50'
      }`}
      title={body}
    >
      {body}
    </td>
  )
}
